import { createContext, useState, useEffect } from "react";
import { db, auth } from "../firebase/config";
import { client } from "../hooks/Client";
import { useHistory } from "react-router-dom";
import { useLocation } from "react-router-dom";
import notDeleted from "../helpers/notDeleted";
import isPublicPath from "../helpers/isPublicPath";

export const Auth = createContext()

export const AuthProvider = (props) => {
    const [authO, setAuthO] = useState(null)

    const history = useHistory()
    const location = useLocation()

    const locationArray = location.pathname.split("/")

    const urlOne = locationArray[1]
    const urlTwo = locationArray[2]
    const urlThree = locationArray[3]
    const urlFour= locationArray[4]
    const urlFive = locationArray[5]
    const urlSix = locationArray[6]
    const urlSeven = locationArray[7]
    const urlEight = locationArray[8]

    // This is the main function that checks for user authentication and gets user info accordingly.
    // Geeft de opzegfunctie van de listener terug; het effect onderaan ruimt
    // hem op. Zonder dat bleef er bij elke padwijziging een listener achter,
    // en die kunnen daarna allemaal onafhankelijk naar het inlogscherm sturen.
    const getUserInfo = () =>
        auth.onAuthStateChanged(async user => {
        if (!user) {
            handleUnauthenticatedUser();
            return;
        }
        await handleAuthenticatedUser(user);
        });

  // Users-doc van de ingelogde gebruiker, alleen wanneer die de SuperAdmin-vlag
  // draagt. uid-keyed: Users-docs staan op Users/{uid}, wat immuun is voor
  // dubbele e-mailadressen.
  // AI-CONTEXT: See docs/pitfalls.md#security-duplicate-users-by-email
  const resolveSuperAdmin = async (user) => {
    const snapshot = await db.collection("Users").doc(user.uid).get();

    if (!snapshot.exists) return null;

    const data = snapshot.data();

    if (data.SuperAdmin !== true || !notDeleted(data)) return null;

    return data;
  };

  // This function handles the logic for authenticated users to retrieve their information from the database.
  const handleAuthenticatedUser = async (user) => {
    try {
      const userQuerySnapshot = await db.collection("Users")
        .where("Compagny", "array-contains", client)
        .where("Email", "==", user.email)
        .get();

      // Exclude soft-deleted Users docs (ADR-008) — a deactivated account must
      // not resolve a session.
      const activeUserDocs = userQuerySnapshot.docs.filter(doc => notDeleted(doc.data()));

      if (activeUserDocs.length === 0) {
        // Deccos-superadmins mogen een tenant openen zonder teamlid te zijn.
        // Dit is alleen de UI-laag — de echte grens is de Firestore-rule, die
        // een lopende superadmin-sessie (claim `suT`/`suE`) eist. Zonder sessie
        // komt de gebruiker binnen bij een lege omgeving plus de banner die
        // uitlegt hoe je er een start.
        // AI-CONTEXT: See docs/features/superadmin-tenant-access.md
        const superAdmin = await resolveSuperAdmin(user);

        if (superAdmin) {
          setAuthO(superAdmin);
          return;
        }

        handleNonCompanyMember();
        return;
      }

      activeUserDocs.forEach(doc => {
        setAuthO(doc.data());
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to check company membership:", error);
    }
  };


  // This function deals with unauthenticated users and decides if they should be redirected.
  const handleUnauthenticatedUser = () => {
    if (isPublicPage()) {
      handlePublicPageAccess();
    } else {
      redirectToLogin();
    }
  };

  // Het inlogscherm krijgt mee waar de gebruiker heen wilde. Dit is de plek
  // waar die bestemming verloren ging -- nog voordat Login.js draaide -- en
  // dus ook de plek waar hij bewaard moet worden. Login.js keurt de waarde
  // daarna met safeNextPath: alleen een pad op deze site, en alleen in een
  // organisatie waar de gebruiker lid van is.
  // AI-CONTEXT: See docs/features/updates-mailing.md
  const loginUrlWithNext = () => {
    if (location.pathname === '/login') return '/login';
    const bestemming = `${location.pathname}${location.search || ''}`;
    return `/login?next=${encodeURIComponent(bestemming)}`;
  };

  // This function contains the logic to be executed when the user is authenticated but not a member of the company.
  const handleNonCompanyMember = () => {
    if (!isPublicPage()) {
      history.push(loginUrlWithNext());
    }
  };
  
  // This function checks whether the current page is one of the public pages where authentication is not required.
  // AI-CONTEXT: See docs/pitfalls.md#routing-public-page-allowlist-miss
  // Adding a public route requires updating PublicRoutes.jsx AND helpers/isPublicPath.js.
  const isPublicPage = () => isPublicPath(location.pathname);
  
  // Handles the logic if a user is on a public page, which may require different actions or logging.
  const handlePublicPageAccess = () => {
    // Additional logic for public pages can be added here if needed
  };

  // Redirects the user to the login page, to be used when an unauthenticated user attempts to access a restricted page.
  const redirectToLogin = () => {
    history.push(loginUrlWithNext());
  };
  
  // useEffect is used here to ensure that the user information is checked whenever the specified URLs change.
  useEffect(() => {
    const unsubscribe = getUserInfo();
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [urlOne, urlTwo, urlThree, urlFour, urlFive, urlSix, urlSeven, urlEight]);
  

    return(
        <Auth.Provider value={[authO]}>
            {props.children}
        </Auth.Provider>
    )
}